import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type PlatformRole = 'super_admin' | 'relationship_manager' | 'manager' | null;

interface PlatformRoleState {
  role: PlatformRole;
  loading: boolean;
  isPlatformUser: boolean;
  isSuperAdmin: boolean;
  isRelationshipManager: boolean;
  isManager: boolean;
  assignedMerchantIds: string[];
}

export function usePlatformRole(): PlatformRoleState {
  const { user } = useAuth();
  const [role, setRole] = useState<PlatformRole>(null);
  const [loading, setLoading] = useState(true);
  const [assignedMerchantIds, setAssignedMerchantIds] = useState<string[]>([]);

  useEffect(() => {
    const fetchRole = async () => {
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        // Get platform admin record with role
        const { data: adminData, error: adminError } = await supabase
          .from('platform_admins')
          .select('id, role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (adminError || !adminData) {
          setRole(null);
          setLoading(false);
          return;
        }

        setRole(adminData.role as PlatformRole);

        // If relationship manager, fetch assigned merchants
        if (adminData.role === 'relationship_manager') {
          const { data: assignments } = await supabase
            .from('merchant_assignments')
            .select('merchant_id')
            .eq('platform_user_id', adminData.id);

          if (assignments) {
            setAssignedMerchantIds(assignments.map(a => a.merchant_id));
          }
        }
      } catch (error) {
        console.error('Error fetching platform role:', error);
        setRole(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [user]);

  return {
    role,
    loading,
    isPlatformUser: role !== null,
    isSuperAdmin: role === 'super_admin',
    isRelationshipManager: role === 'relationship_manager',
    isManager: role === 'manager',
    assignedMerchantIds,
  };
};
